import React, { useState } from 'react';
import { Form, Input, Select, message } from 'antd';
import { objectKeys } from 'src/utils/objectKeys';
import { objectFromEntries } from 'src/utils/objectFromEntries';
import { defaultErrorMessage } from 'src/constants/errorMessages';
import { FormModal } from '../FormModal';
import ProductSegment from '../types/ProductSegment';
import { getMessage } from 'src/utils/ErrorUtils';

const { Option } = Select;

export type FormSegmentProps = {
  productSegment: ProductSegment | null;
  closeModal: () => void;
  onSubmitForm: (data: FormSegmentData, changes: Partial<FormSegmentData>) => Promise<void>;
  formTitle: string;
  successMessage: string;
  disableSubmitWhenNoChanges?: boolean;
  renderFormFooter: (disabledSubmit: boolean, formData: FormSegmentData) => React.ReactNode;
  onProductNameChange?: (val: string) => void;
};

type ErrorState = {
  [key: string]: string | null;
};

export type FormSegmentData = {
  productSegmentName: string;
  productSegmentDescription: string;
  active: boolean;
  favourite: boolean;
  shared: boolean;
};

export type FormSegmentValues = {
  productName: string;
  productDescription: string;
  status: Enabled;
  favorite: Enabled;
  shared: Enabled;
};

enum Enabled {
  Yes = '1',
  No = '0',
}

const duplicateWord = 'Duplicate';
const errorDuplicateMessage = 'A product segment already exists with that name within your organization. Please update the segment name.';

const defaultValues: FormSegmentValues = {
  productName: '',
  productDescription: '',
  status: Enabled.Yes,
  favorite: Enabled.No,
  shared: Enabled.No,
};

const getFormSegmentData = (values: FormSegmentValues): FormSegmentData => {
  return {
    productSegmentName: values.productName,
    productSegmentDescription: values.productDescription,
    active: !!Number(values.status),
    favourite: !!Number(values.favorite),
    shared: !!Number(values.shared),
  };
};

const getFormSegmentPartialData = (values: Partial<FormSegmentValues>): Partial<FormSegmentData> => {
  const mapping: { [K in keyof FormSegmentValues]: keyof FormSegmentData } = {
    productName: 'productSegmentName',
    productDescription: 'productSegmentDescription',
    status: 'active',
    favorite: 'favourite',
    shared: 'shared',
  };
  const booleanKeys: Array<keyof FormSegmentValues> = ['status', 'favorite', 'shared'];
  return objectFromEntries(
    objectKeys(values).map((key) => [
      mapping[key], //
      booleanKeys.includes(key) ? !!Number(values[key]) : values[key],
    ])
  );
};

const getChangedFormEntries = <T extends FormSegmentValues>(initial: T, current: T): [keyof T, T[keyof T]][] => {
  return objectKeys(initial)
    .filter((key) => current[key] !== initial[key])
    .map((key) => [key, current[key]]);
};

export const FormSegment: React.FC<FormSegmentProps> = ({
  productSegment,
  closeModal,
  onSubmitForm,
  formTitle,
  successMessage,
  disableSubmitWhenNoChanges,
  renderFormFooter,
  onProductNameChange,
}) => {
  const initialValues: FormSegmentValues = {
    ...defaultValues,
    ...(productSegment && {
      productName: productSegment.productSegmentName,
      productDescription: productSegment.productSegmentDescription || '',
      status: productSegment.active ? Enabled.Yes : Enabled.No,
      favorite: productSegment.favourite ? Enabled.Yes : Enabled.No,
      shared: productSegment.shared ? Enabled.Yes : Enabled.No,
    }),
  };

  const [isOpen, setIsOpen] = useState(true);
  const [productName, setProductName] = useState(initialValues.productName);
  const [productDescription, setProductDescription] = useState(initialValues.productDescription);
  const [status, setStatus] = useState(initialValues.status);
  const [favorite, setFavorite] = useState(initialValues.favorite);
  const [shared, setShared] = useState(initialValues.shared);

  const [error, setError] = useState<ErrorState>({
    productName: null,
  });

  const currentValues: FormSegmentValues = {
    productName,
    productDescription,
    status,
    favorite,
    shared,
  };

  const getChangedValues = (): Partial<FormSegmentValues> => {
    return objectFromEntries(getChangedFormEntries(initialValues, currentValues));
  };

  const isSubmitDisabled = (): boolean => {
    const isEmptyProductName = productName.trim() === '';

    if (disableSubmitWhenNoChanges) {
      const entries = getChangedFormEntries(initialValues, currentValues);
      return isEmptyProductName || entries.length === 0;
    }
    return isEmptyProductName;
  };

  const hideModal = () => {
    setIsOpen(false);
  };

  const handleChangeSegmentName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductName(e.target.value);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onProductNameChange?.(e.target.value);
    if (productName) {
      setError({ ...error, productName: null });
    }
  };

  const handleAfterVisibleChange = (visible: boolean) => {
    if (!visible) {
      if (closeModal) {
        closeModal();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setError({
      productName: productName ? null : 'Product Segment Name is required!',
    });

    if (productName) {
      const messageOptions = { content: '', formMessageKey: 'formState', duration: 2 };
      const hideLoading = message.loading('Loading...', 0);

      try {
        await onSubmitForm(getFormSegmentData(currentValues), getFormSegmentPartialData(getChangedValues()));
        message.success({ ...messageOptions, content: successMessage });
        hideModal();
      } catch (e) {
        // TODO need to standartize our error messages (which ones and when to show)
        // when to show a default error message or a specific one
        let errorMessage = defaultErrorMessage;
        if (getMessage(e)?.includes(duplicateWord)) {
          errorMessage = errorDuplicateMessage;
        }
        message.error({
          ...messageOptions,
          content: errorMessage,
        });
        console.error(e);
      } finally {
        hideLoading();
      }
    }
  };

  return (
    <FormModal isModalOpen={isOpen} hideModal={hideModal} afterVisibleChange={handleAfterVisibleChange}>
      <h1>{formTitle}</h1>
      <Form name="FormSegment" onSubmit={handleSubmit} autoComplete="off">
        <Form.Item
          label="Product Segment Name:"
          required
          validateStatus={error.productName ? 'error' : ''}
          help={error.productName}
        >
          <Input name="product-name" value={productName} onChange={handleChangeSegmentName} />
        </Form.Item>
        <Form.Item label="Product Segment Description:">
          <Input
            name="product-description"
            value={productDescription}
            onChange={(e) => setProductDescription(e.target.value)}
          />
        </Form.Item>
        <Form.Item label="Status:">
          <Select value={status} onChange={(value) => setStatus(value)}>
            <Option value={Enabled.Yes}>Active</Option>
            <Option value={Enabled.No}>Inactive</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Set As Favorite:">
          <Select value={favorite} onChange={(value) => setFavorite(value)}>
            <Option value={Enabled.Yes}>Yes</Option>
            <Option value={Enabled.No}>No</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Shared:">
          <Select value={shared} onChange={(value) => setShared(value)}>
            <Option value={Enabled.Yes}>Yes</Option>
            <Option value={Enabled.No}>No</Option>
          </Select>
        </Form.Item>
        <Form.Item>{renderFormFooter(isSubmitDisabled(), getFormSegmentData(currentValues))}</Form.Item>
      </Form>
    </FormModal>
  );
};
